import moment from 'moment/moment'

import { reportingColors } from '../../../utils/colors'
import { Point, drawRoundedRect } from '../../../utils/visualization'

const BORDER_RADIUS = 4
export const DEFAULT_BAR_SPACING = 8

export enum BarElementState {
    NEUTRAL,
    SELECTED,
    UNSELECTED,
}

interface BarElementOptions {
    origin: Point
    width: number
    maxBarHeight: number
    value: number
    maxValue: number
    label: moment.Moment
    color: string
    state?: BarElementState
}

export class BarElement {
    private barColor0: string = reportingColors.gray200
    private barColor1: string = reportingColors.blue400
    private barElement: HTMLDivElement
    private state: BarElementState = BarElementState.NEUTRAL
    private value: number = 0
    private label: moment.Moment | null
    private height: number = 0
    private maxBarHeight: number = 0

    public fromDate: Date | null
    public toDate: Date | null
    public centerHorizontal: number = 0
    public origin: Point = { x: 0, y: 0 }
    public width: number = 0
    public spacing: number = DEFAULT_BAR_SPACING

    constructor() {
        this.barElement = document.createElement('div')
        this.barElement.style.backgroundColor = this.barColor0
        this.barElement.style.borderTopLeftRadius = `${BORDER_RADIUS}px`
        this.barElement.style.borderTopRightRadius = `${BORDER_RADIUS}px`
        this.fromDate = null
        this.toDate = null
        this.label = null
        this.reset()
    }

    public setValues({
        origin,
        width,
        maxBarHeight,
        value,
        maxValue,
        label,
        color,
        state,
    }: BarElementOptions) {
        this.value = value
        this.label = label
        this.barColor1 = color
        this.width = width
        this.maxBarHeight = maxBarHeight
        this.height = (value / maxValue) * maxBarHeight
        this.centerHorizontal = origin.x + width / 2
        this.origin.y = this.maxBarHeight - this.height + origin.y
        this.origin.x = origin.x
        this.fromDate = label.toDate()
        this.state = state || BarElementState.NEUTRAL
        this.updateBarColor()
    }

    public getValue() {
        return this.value
    }

    public getBarHeight() {
        return this.height
    }

    public getLabelDate() {
        return this.label?.toDate()
    }

    private updateBarColor() {
        switch (this.state) {
            case BarElementState.SELECTED:
                this.barElement.style.backgroundColor = this.barColor1
                break
            case BarElementState.UNSELECTED:
                this.barElement.style.backgroundColor = this.barColor0
                break
            case BarElementState.NEUTRAL:
                this.barElement.style.backgroundColor = this.barColor1
                break
        }
    }

    public draw(ctx: CanvasRenderingContext2D) {
        const x = this.origin.x
        const y = this.origin.y
        const width = this.width
        const height = this.height
        const radius = 4
        const fillStyle = this.barElement.style.backgroundColor
        drawRoundedRect(ctx, x, y, width, height, radius, fillStyle, false)
    }

    public reset() {
        this.value = 0
        this.label = null
        this.fromDate = null
        this.toDate = null
        this.setState(BarElementState.NEUTRAL)
    }

    public setState(state: BarElementState) {
        this.state = state
        this.updateBarColor()
    }

    public top() {
        return {
            x: parseFloat(this.barElement.style.width) / 2,
            y: parseFloat(this.barElement.style.height),
        }
    }
}
